import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Calendar, SlidersHorizontal, X, Bed, Home, DollarSign } from 'lucide-react';
import { getProperties, getAreas } from '@/lib/api';
import PropertyCard from '@/components/PropertyCard';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const RentalsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [locations, setLocations] = useState([]);
  const { formatPriceShort } = useCurrency();
  
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    bedrooms: searchParams.get('bedrooms') || '',
    propertyType: searchParams.get('property_type') || '',
    rentalPeriod: searchParams.get('rental_type') || 'monthly',
    maxPrice: searchParams.get('max_price') || '',
    sort: searchParams.get('sort') || 'newest',
  });

  const propertyTypes = [
    { value: 'villa', label: 'Villa' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'house', label: 'House' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'boutique_hotel', label: 'Boutique Hotel' },
  ];
  const bedroomOptions = [
    { value: '1', label: '1 Bedroom' },
    { value: '2', label: '2 Bedrooms' },
    { value: '3', label: '3 Bedrooms' },
    { value: '4', label: '4+ Bedrooms' },
  ];
  const monthlyPriceRanges = [
    { value: '0-2000', label: `Under ${formatPriceShort(2000)}/mo` },
    { value: '2000-5000', label: `${formatPriceShort(2000)} - ${formatPriceShort(5000)}/mo` },
    { value: '5000-10000', label: `${formatPriceShort(5000)} - ${formatPriceShort(10000)}/mo` },
    { value: '10000-99999999', label: `${formatPriceShort(10000)}+/mo` },
  ];
  const yearlyPriceRanges = [
    { value: '0-20000', label: `Under ${formatPriceShort(20000)}/yr` },
    { value: '20000-50000', label: `${formatPriceShort(20000)} - ${formatPriceShort(50000)}/yr` },
    { value: '50000-100000', label: `${formatPriceShort(50000)} - ${formatPriceShort(100000)}/yr` },
    { value: '100000-99999999', label: `${formatPriceShort(100000)}+/yr` },
  ];
  const sortOptions = [
    { value: 'newest', label: 'Newest' },
    { value: 'price_asc', label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
    { value: 'bedrooms', label: 'Bedrooms' },
  ];

  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await getAreas(true);
        setLocations(res.data.map(a => a.name));
      } catch (err) {
        setLocations(['Canggu', 'Berawa', 'Pererenan', 'Seminyak', 'Uluwatu', 'Bingin', 'Sanur', 'Ubud']);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filters.location) params.location = filters.location;
        if (filters.bedrooms) params.bedrooms = filters.bedrooms;
        if (filters.propertyType) params.property_type = filters.propertyType;
        params.sort = filters.sort;

        const res = await getProperties(params);
        // Filter properties that have long-term rental status OR monthly/yearly rental prices
        const filtered = res.data.filter(p => {
          const isLongTermStatus = p.status === 'for_rent_long';
          const hasMonthlyPrice = filters.rentalPeriod === 'monthly' && p.monthly_rental_price;
          const hasYearlyPrice = filters.rentalPeriod === 'yearly' && p.yearly_rental_price;
          return isLongTermStatus || hasMonthlyPrice || hasYearlyPrice;
        }).filter(p => 
          (filters.rentalPeriod === 'monthly' && p.monthly_rental_price) ||
          (filters.rentalPeriod === 'yearly' && p.yearly_rental_price)
        );
        
        // Apply price filter
        let result = filtered;
        if (filters.maxPrice) {
          const [min, max] = filters.maxPrice.split('-').map(Number);
          result = filtered.filter(p => {
            const price = filters.rentalPeriod === 'monthly' 
              ? p.monthly_rental_price 
              : p.yearly_rental_price;
            return price >= min && price <= max;
          });
        }
        
        setProperties(result);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error('Rentals fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [filters]);

  const updateFilters = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      bedrooms: '',
      propertyType: '',
      rentalPeriod: 'monthly',
      maxPrice: '',
      sort: 'newest',
    });
    setSearchParams({});
  };

  const activeFilterCount = [
    filters.location,
    filters.bedrooms,
    filters.propertyType,
    filters.maxPrice,
  ].filter(Boolean).length;

  const priceRanges = filters.rentalPeriod === 'monthly' ? monthlyPriceRanges : yearlyPriceRanges;

  return (
    <div className="min-h-screen bg-bali-white pt-20">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-temple-gold/10 to-warm-sand py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-charcoal mb-4">
              Long-term Rentals in Bali
            </h1>
            <p className="font-body text-charcoal-light max-w-2xl mx-auto">
              Find your perfect monthly or yearly rental. Villas and apartments available for extended stays.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Rental Period Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-warm-sand rounded-full p-1">
            <button
              onClick={() => updateFilters('rentalPeriod', 'monthly')}
              className={`px-6 py-2 rounded-full font-medium text-sm transition-all ${
                filters.rentalPeriod === 'monthly'
                  ? 'bg-ubud-jungle text-white'
                  : 'text-charcoal hover:bg-white/50'
              }`}
            >
              Monthly Rentals
            </button>
            <button
              onClick={() => updateFilters('rentalPeriod', 'yearly')}
              className={`px-6 py-2 rounded-full font-medium text-sm transition-all ${
                filters.rentalPeriod === 'yearly'
                  ? 'bg-ubud-jungle text-white'
                  : 'text-charcoal hover:bg-white/50'
              }`}
            >
              Yearly Rentals
            </button>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap items-center gap-3">
            {/* Location */}
            <Select value={filters.location || "all"} onValueChange={(v) => updateFilters('location', v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] bg-white">
                <MapPin className="w-4 h-4 mr-2 text-charcoal-muted" />
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Bedrooms */}
            <Select value={filters.bedrooms || "all"} onValueChange={(v) => updateFilters('bedrooms', v === "all" ? "" : v)}>
              <SelectTrigger className="w-[140px] bg-white">
                <Bed className="w-4 h-4 mr-2 text-charcoal-muted" />
                <SelectValue placeholder="Beds" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                {bedroomOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Property Type */}
            <Select value={filters.propertyType || "all"} onValueChange={(v) => updateFilters('propertyType', v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] bg-white">
                <Home className="w-4 h-4 mr-2 text-charcoal-muted" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {propertyTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price Range */}
            <Select value={filters.maxPrice || "all"} onValueChange={(v) => updateFilters('maxPrice', v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] bg-white">
                <DollarSign className="w-4 h-4 mr-2 text-charcoal-muted" />
                <SelectValue placeholder="Price" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Price</SelectItem>
                {priceRanges.map(range => (
                  <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-charcoal-muted hover:text-charcoal"
              >
                <X className="w-4 h-4 mr-1" />
                Clear ({activeFilterCount})
              </Button>
            )}
          </div>

          {/* Sort */}
          <Select value={filters.sort} onValueChange={(v) => updateFilters('sort', v)}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="font-body text-charcoal-light">
            {loading ? 'Loading...' : `${properties.length} ${filters.rentalPeriod} rental${properties.length !== 1 ? 's' : ''} found`}
          </p>
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-warm-sand rounded-xl h-80 animate-pulse" />
            ))}
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 text-charcoal-muted mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-charcoal mb-2">
              No {filters.rentalPeriod} rentals found
            </h3>
            <p className="font-body text-charcoal-light mb-6">
              Try adjusting your filters or switching between monthly and yearly rentals.
            </p>
            <Button onClick={clearFilters} variant="outline">
              Clear Filters
            </Button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {properties.map((property, index) => (
              <motion.div
                key={property.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <PropertyCard 
                  property={property} 
                  priceType={filters.rentalPeriod}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RentalsPage;
